// Austrian Locations - API-backed (uses MongoDB-seeded Statistik Austria data)
// Backwards-compatible with the original static-data API.
//
// Call `initLocations()` once at app startup. All getters return cached arrays.

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module-level cache
let _states = [];
let _districtsByState = {};
let _gemeindenByDistrict = {};
let _gemeindeCoords = {}; // key: `${district}|${gemeinde}` -> {lat, lng}
let _districtCoords = {}; // key: district -> {lat, lng}
let _initPromise = null;

export const AUSTRIAN_STATES = [
  'Wien',
  'Niederösterreich',
  'Oberösterreich',
  'Steiermark',
  'Tirol',
  'Salzburg',
  'Kärnten',
  'Vorarlberg',
  'Burgenland',
];

/**
 * Initialize location data from backend. Idempotent: returns cached promise on repeated calls.
 */
export function initLocations() {
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    try {
      const res = await axios.get(`${API}/locations/all`);
      const data = res.data || {};
      _states = data.states || AUSTRIAN_STATES;
      _districtsByState = data.districts || {};
      
      // Reshape gemeinden cache + coords lookups
      const gemeinden = data.gemeinden || {};
      Object.entries(gemeinden).forEach(([district, gems]) => {
        _gemeindenByDistrict[district] = gems;
        if (gems.length > 0) {
          const avgLat = gems.reduce((s, g) => s + g.lat, 0) / gems.length;
          const avgLng = gems.reduce((s, g) => s + g.lng, 0) / gems.length;
          _districtCoords[district] = { lat: avgLat, lng: avgLng };
        }
        gems.forEach((g) => {
          _gemeindeCoords[`${district}|${g.gemeinde}`] = { lat: g.lat, lng: g.lng };
        });
      });
      
      return true;
    } catch (err) {
      console.error('Failed to load Austrian locations:', err);
      _states = AUSTRIAN_STATES;
      return false;
    }
  })();

  return _initPromise;
}

/** Returns array of district names for a state. Empty array if not loaded yet. */
export const getDistrictsForState = (state) => _districtsByState[state] || [];

/** Returns array of gemeinde objects {name, lat, lng} for a district. */
export const getGemeindenForDistrict = (district) => {
  const gems = _gemeindenByDistrict[district] || [];
  // Convert API shape {gemeinde,lat,lng,gkz} to legacy shape {name,lat,lng}
  return gems.map((g) => ({ name: g.gemeinde, lat: g.lat, lng: g.lng }));
};

/** Returns {lat, lng} for state+district+optional gemeinde, or null. */
export const getLocationCoordinates = (state, district, gemeinde) => {
  if (gemeinde && district) {
    const c = _gemeindeCoords[`${district}|${gemeinde}`];
    if (c) return c;
  }
  if (district) {
    const c = _districtCoords[district];
    if (c) return c;
  }
  return null;
};

/** Distance in km between two coordinates (Haversine). */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Legacy exports (now empty objects - data fetched dynamically)
export const DISTRICTS_BY_STATE = _districtsByState;
export const GEMEINDEN_BY_DISTRICT = _gemeindenByDistrict;
